/**
 * Klaviyo 专用加载器：在 ConsentAppLoader 同意之后才注入 klaviyo.js。
 *
 * 与 ConsentAppLoader 的分工：
 * - ConsentAppLoader：何时可以追踪（用户是否点 Accept）
 * - KlaviyoConsentLoader：如何加载 Klaviyo + SDK 何时可用（script onload 之后）
 *
 * 全站单例，只插入一个 script 标签。业务代码请用 whenReady(fn)，不要自行 load SDK。
 *
 * @see ConsentAppLoader — 下层 consent 门控，本类不重复实现同意逻辑
 */
class KlaviyoConsentLoader {
  static _loaded = false;
  static _loading = false;
  static _queue = [];
  static _initialized = false;

  static init() {
    if (this._initialized) return;
    this._initialized = true;

    const companyId = window.theme?.klaviyo?.companyId;
    if (!companyId) return;

    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.load();
      return;
    }

    ConsentAppLoader.whenConsented(() => this.load());
  }

  static load() {
    if (this._loaded || this._loading) return;

    const companyId = window.theme?.klaviyo?.companyId;
    if (!companyId) return;

    this._loading = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://static.klaviyo.com/onsite/js/${companyId}/klaviyo.js`;

    script.onload = () => {
      this._loading = false;
      this._loaded = true;

      const email = window.theme?.klaviyo?.customerEmail;
      if (email && window.klaviyo?.identify) {
        window.klaviyo.identify({ $email: email });
      }

      document.dispatchEvent(new CustomEvent('klaviyoLoaded'));
      this._flushQueue();
    };

    script.onerror = () => {
      this._loading = false;
      console.error('KlaviyoConsentLoader: failed to load klaviyo.js');
    };

    document.head.appendChild(script);
  }

  static _flushQueue() {
    this._queue.forEach((fn) => fn());
    this._queue = [];
  }

  static whenReady(fn) {
    if (!this._initialized) this.init();

    const companyId = window.theme?.klaviyo?.companyId;
    if (!companyId) return;

    if (this._loaded) {
      fn();
      return;
    }

    this._queue.push(fn);

    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.load();
    }
  }

  static isLoaded() {
    return this._loaded === true;
  }
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  KlaviyoConsentLoader.init();
} else {
  document.addEventListener('DOMContentLoaded', () => KlaviyoConsentLoader.init());
}
